import { Icon } from "@iconify/react";
import MarkdownPreview from "@uiw/react-markdown-preview";
import { DateTime } from "luxon";
import { Copier } from "../../../../components/common/Copier";
import type { Message as MessageType } from "./types";

interface MessageProps {
  message: MessageType;
  isDarkMode: boolean;
  isStreaming?: boolean;
}

// Custom component renderers for markdown elements
function createCustomComponents(isDarkMode: boolean) {
  return {
    // Customize headings
    h1: ({ children, ...props }: any) => {
      return (
        <h1
          className={`text-xl! font-bold mt-2 mb-2 ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
          {...props}
        >
          {children}
        </h1>
      );
    },
    h2: ({ children, ...props }: any) => {
      return (
        <h2
          className={`text-xl! font-bold mt-2 mb-2 ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
          {...props}
        >
          {children}
        </h2>
      );
    },
    h3: ({ children, ...props }: any) => {
      return (
        <h3
          className={`text-xl! font-semibold mt-2 mb-2 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
          {...props}
        >
          {children}
        </h3>
      );
    },
    h4: ({ children, ...props }: any) => {
      return (
        <h4
          className={`text-xl! font-semibold mt-2 mb-2 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
          {...props}
        >
          {children}
        </h4>
      );
    },
    // Customize paragraphs
    p: ({ children, ...props }: any) => {
      return (
        <p
          className={`mb-1! leading-relaxed ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
          {...props}
        >
          {children}
        </p>
      );
    },
    // Customize links
    a: ({ href, children, ...props }: any) => {
      return (
        <a
          href={href}
          className={`underline ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
          target="_blank"
          rel="noopener noreferrer"
          {...props}
        >
          {children}
        </a>
      );
    },
    // Customize lists
    ul: ({ children, ...props }: any) => {
      return (
        <ul className={`ml-3 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`} {...props}>
          {children}
        </ul>
      );
    },
    li: ({ children, className, ...props }: any) => {
      const hasQuotationQuestionClass = className?.includes("quotation-question") ?? false;

      const listStyles = !hasQuotationQuestionClass ? "list-disc list-inside" : "";
      const colorStyles = hasQuotationQuestionClass
        ? "bg-red-400! text-white! rounded-lg! px-2 py-1"
        : isDarkMode
          ? "text-gray-200"
          : "text-gray-800";

      return (
        <li className={`${listStyles} items-center ${colorStyles}`} {...props}>
          {hasQuotationQuestionClass ? (
            <span className="inline-grid gap-2 grid-cols-[auto_auto] items-center auto-cols-max">
              <Icon
                icon="mdi:question-mark"
                className="w-4 h-4 bg-white rounded-full p-0.5 text-red-400"
              />
              {children}
            </span>
          ) : (
            children
          )}
        </li>
      );
    },
    ol: ({ children, ...props }: any) => {
      return (
        <ol className={`ml-3 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`} {...props}>
          {children}
        </ol>
      );
    },
    // Customize blockquotes
    blockquote: ({ children, ...props }: any) => {
      const borderColor = isDarkMode ? "border-gray-600" : "border-gray-300";
      const bgColor = isDarkMode ? "bg-gray-800" : "bg-gray-50";
      return (
        <blockquote
          className={`border-l-4 ${borderColor} pl-4 ml-0 ${bgColor} py-2 px-4 rounded`}
          {...props}
        >
          {children}
        </blockquote>
      );
    },
    // Customize div with quotation-ready-notice class
    div: ({ className, children, ...props }: any) => {
      if (className?.includes("quotation-ready-notice")) {
        return (
          <div
            className="bg-green-600 text-white rounded-lg px-4 py-2 grid grid-cols-[auto_1fr] gap-2 items-center shadow-md shadow-green-500/50"
            {...props}
          >
            <Icon icon="mdi:check" className="w-4 h-4 bg-white rounded-full p-0.5 text-green-600" />
            {children}

            <p className="inline-grid grid-cols-[auto_auto] items-center auto-cols-max col-span-2">
              <span>
                You can now generate a quotation for this request by clicking the button below.
              </span>
              <Icon
                icon="mdi:file-document-edit-outline"
                className="w-10 h-10 bg-green-700 rounded-lg p-2 text-white"
              />
            </p>
          </div>
        );
      }
      return <div {...props}>{children}</div>;
    },
    // Customize span with duration-estimate class
    span: ({ className, children, ...props }: any) => {
      if (className?.includes("duration-estimate")) {
        return (
          <span
            className="bg-yellow-400 text-yellow-900 rounded-md px-2 py-1 font-medium inline-block text-xs"
            {...props}
          >
            {children}
          </span>
        );
      }
      return <span {...props}>{children}</span>;
    },
  };
}

function Message({ message, isDarkMode, isStreaming = false }: MessageProps) {
  const defaultAvatarColor = isDarkMode ? "bg-blue-600" : "bg-blue-500";
  const userAvatarColor = isDarkMode ? "bg-green-600" : "bg-green-500";
  const avatarClassName = message.role === "user" ? userAvatarColor : defaultAvatarColor;
  const iconName = message.role === "user" ? "mdi:account" : "mdi:chat";

  return (
    <div className="grid grid-cols-[auto_1fr] gap-2 md:gap-3 min-w-0">
      <div className={`w-8 h-8 rounded-full ${avatarClassName} grid place-items-center`}>
        <Icon icon={iconName} className="w-5 h-5 text-white" />
      </div>
      <div className="grid grid-rows-[auto_auto_auto] gap-1 min-w-0">
        <div
          className={`rounded-lg p-2 md:p-3 shadow-sm min-w-0 relative group ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <Copier content={message.content} />
          </div>
          <div className="pr-3 break-words">
            <MarkdownPreview
              source={message.content}
              wrapperElement={{
                "data-color-mode": isDarkMode ? "dark" : "light",
              }}
              className={`${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
              style={isDarkMode ? { backgroundColor: "#1E2939" } : undefined}
              components={createCustomComponents(isDarkMode)}
            />
          </div>
        </div>
        {isStreaming && (
          <div className="text-xs italic text-purple-500 pl-1 animate-pulse drop-shadow-[0_0_8px_rgba(168,85,247,0.8)]">
            Thinking...
          </div>
        )}
        <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
          {DateTime.fromISO(message.timestamp).toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)}
        </span>
      </div>
    </div>
  );
}

export { Message };
